import React, { useMemo } from "react";
import { addDays, toISO, fmtRoDay, isSaturdayIso, regieColor, regieDefaultMinForIso } from "./utils";

const DOW_HEADERS = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];

/**
 * Vedere săptămânală: fiecare regie/proiect pe o singură linie orizontală.
 * Coloane: 7 zile (Lu-Du). Celule: count alocați vs necesari + click pentru detalii.
 *
 * Props:
 *  - anchor: Date — luni săpt.
 *  - regii, projects: lista filtrată (deja sortată cu compareRegii)
 *  - entries: schedule_entries care intersectează săptămâna
 *  - onCellClick(isoDay, subjectId, subjectType): deschide CreateDayDialog pe acea zi
 *  - isRegiePausedOn, isProjectInDismantle: helpers din Planificator
 *  - holidaysByDate: { 'YYYY-MM-DD': { type } } — pentru a marca duminicile/sărbătorile
 *  - regiiWeeks, regiiDayOverrides: pentru calculul necesarului
 */
export default function WeeklyMatrix({
  anchor,
  regii,
  projects,
  entries,
  onCellClick,
  onShareDay,
  onTitleClick,
  isRegiePausedOn,
  isProjectInDismantle,
  holidaysByDate = {},
  regiiWeeks = [],
  regiiDayOverrides = [],
  projectsDayOverrides = [],
}) {
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = addDays(anchor, i);
      out.push({ date: d, iso: toISO(d), dow: i });
    }
    return out;
  }, [anchor]);

  // Helper: găsește entry-ul pentru (regie/proiect, day) — direct sau ca interval
  const findEntry = (subjectId, type, isoDay) => {
    return (entries || []).find((e) => {
      if (type === "regie" && e.regie_id !== subjectId) return false;
      if (type === "project" && e.project_id !== subjectId) return false;
      const sd = e.start_date || "";
      const ed = e.end_date || sd;
      return sd <= isoDay && isoDay <= ed;
    });
  };

  const needsForCell = (subject, type, isoDay) => {
    if (type === "regie") {
      const r = subject;
      const dov = regiiDayOverrides.find((o) => o.regie_id === r.id && o.date === isoDay);
      const wkOv = regiiWeeks.find((w) => w.regie_id === r.id);
      const minP = dov && dov.min_people != null
        ? Number(dov.min_people)
        : (wkOv && wkOv.min_people != null ? Number(wkOv.min_people) : regieDefaultMinForIso(r, isoDay));
      // sâmbăta = 0 (manual)
      if (isSaturdayIso(isoDay)) return { minP: 0, minC: 0, optional: true };
      return { minP, minC: Number(r.default_min_cars || 0), optional: false };
    } else {
      const p = subject;
      if (isProjectInDismantle && isProjectInDismantle(p, isoDay)) return { minP: 0, minC: 0, optional: true };
      // sâmbăta = 0 (alocare manuală)
      if (isSaturdayIso(isoDay)) return { minP: 0, minC: 0, optional: true };
      const dov = projectsDayOverrides.find((o) => o.project_id === p.id && o.date === isoDay);
      const minP = dov && dov.min_people != null ? Number(dov.min_people) : Number(p.min_people || 0);
      return { minP, minC: Number(p.min_cars || 0), optional: false };
    }
  };

  const isSubjectActiveOn = (subject, type, isoDay) => {
    // Sâmbăta = zi normală de lucru: subiectul e activ dacă e în curs oricând
    // în săptămâna respectivă (Lu–Sâ) — alocarea se face manual, cu 0 necesar.
    const sat = isSaturdayIso(isoDay);
    const satDate = sat ? new Date(isoDay + "T00:00:00") : null;
    const monday = sat ? toISO(addDays(satDate, -5)) : null;
    if (type === "regie") {
      const r = subject;
      if (r.active === false) return false;
      if (isRegiePausedOn && isRegiePausedOn(r, isoDay)) return false;
      const sd = r.start_date;
      const ed = r.indeterminate_end ? "9999-12-31" : (r.end_date || sd);
      if (sat) {
        if (sd && sd > isoDay) return false;
        if (ed && ed < monday) return false;
        return true;
      }
      if (sd && isoDay < sd) return false;
      if (ed && isoDay > ed) return false;
      return true;
    } else {
      const p = subject;
      const sd = p.start_date;
      const ed = p.end_date || sd;
      if (sat) {
        if (sd && sd > isoDay) return false;
        if (ed && ed < monday) return false;
        return true;
      }
      if (sd && isoDay < sd) return false;
      if (ed && isoDay > ed) return false;
      return true;
    }
  };

  const renderRow = (subject, type) => {
    const name = subject.name || subject.label || "—";
    const color = type === "regie" ? regieColor(subject) : (subject.color || "#3B82F6");
    const firma = type === "regie" ? (subject.firma || "") : "";

    return (
      <tr key={`${type}-${subject.id}`} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`wm-row-${type}-${subject.id}`}>
        <td className="sticky left-0 bg-white z-10 px-2 py-2 border-r-2 border-zinc-200 min-w-[180px] max-w-[220px]">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="w-1 h-6 rounded-sm flex-shrink-0" style={{ background: color }} />
            <div className="flex flex-col min-w-0 flex-1">
              <span
                className={`text-[12px] font-bold truncate text-zinc-900 ${type === "regie" && onTitleClick ? "cursor-pointer hover:underline hover:text-[var(--primary)]" : ""}`}
                title={type === "regie" && onTitleClick ? `${name} — click pentru istoric (ore, persoane, mașini)` : name}
                onClick={type === "regie" && onTitleClick ? () => onTitleClick(subject, type) : undefined}
                data-testid={`wm-title-${type}-${subject.id}`}
              >{name}</span>
              {firma && <span className="text-[9px] uppercase tracking-wider text-zinc-500 truncate">{firma}</span>}
            </div>
          </div>
        </td>
        {days.map((d) => {
          const hol = holidaysByDate[d.iso];
          const isNatHoliday = hol && hol.type === "national";
          const isSundayReal = d.dow === 6; // addDays from monday: i=6 → Sunday
          // Actually dow here = 0..6 from Monday. So d.dow === 6 means Sunday.
          const isSat = d.dow === 5; // Saturday
          const blackCell = isSundayReal || isNatHoliday;
          const active = isSubjectActiveOn(subject, type, d.iso);
          const entry = active ? findEntry(subject.id, type, d.iso) : null;
          const allocated = (entry?.person_ids || []).length;
          const allocatedCars = (entry?.car_ids || []).length;
          const { minP, minC, optional } = active ? needsForCell(subject, type, d.iso) : { minP: 0, minC: 0, optional: false };
          const understaffed = active && minP > 0 && allocated < minP;
          const fullyStaffed = active && minP > 0 && allocated >= minP;
          const toolHexMap = { rosu: "#EF4444", galben: "#FDE047", albastru: "#3B82F6", verde: "#10B981", gri: "#6B7280", mov: "#A855F7" };
          // Caută culoarea setului în ORICE entry al subiectului pe ziua respectivă (nu doar primul)
          const toolEntry = active ? (entries || []).find((e) => {
            if (type === "regie" && e.regie_id !== subject.id) return false;
            if (type === "project" && e.project_id !== subject.id) return false;
            const sd = e.start_date || "";
            const ed = e.end_date || sd;
            return sd <= d.iso && d.iso <= ed && (e.allocation_color || (e.allocation_colors || []).length);
          }) : null;
          const toolCols = toolEntry ? ((toolEntry.allocation_colors || []).length ? toolEntry.allocation_colors : [toolEntry.allocation_color]) : [];
          const toolHex = toolCols.length ? toolHexMap[toolCols[0]] : null;
          const toolBg = toolCols.length >= 2
            ? `linear-gradient(135deg, ${toolHexMap[toolCols[0]]}40 0%, ${toolHexMap[toolCols[0]]}40 50%, ${toolHexMap[toolCols[1]]}40 50%, ${toolHexMap[toolCols[1]]}40 100%)`
            : (toolHex ? `${toolHex}40` : null);
          let bg = "bg-white";
          let textCls = "text-zinc-700";
          if (blackCell) { bg = "bg-zinc-900"; textCls = "text-zinc-600"; }
          else if (!active) { bg = "bg-white"; textCls = "text-zinc-300"; }
          else if (entry) { bg = "bg-emerald-50"; textCls = "text-emerald-800"; }
          else if (understaffed) { bg = "bg-red-50"; textCls = "text-red-700"; }
          else if (optional) { bg = "bg-amber-50"; textCls = "text-amber-700"; }

          const handleClick = () => {
            if (blackCell || !active) return;
            onCellClick && onCellClick(d.iso, subject.id, type);
          };

          return (
            <td
              key={d.iso}
              className={`${bg} border-r border-zinc-200 align-middle text-center min-w-[88px] h-12 cursor-pointer transition-colors hover:brightness-95`}
              style={toolHex ? { boxShadow: `inset 0 -4px 0 0 ${toolHex}`, background: toolBg } : undefined}
              onClick={handleClick}
              data-testid={`wm-cell-${type}-${subject.id}-${d.iso}`}
              title={!active ? "Inactiv azi" : entry ? `${allocated}/${minP || "—"} pers · ${allocatedCars}/${minC || "—"} 🚗 · click pentru a edita` : `click pentru a aloca${optional ? " (opțional)" : ""}`}
            >
              {blackCell ? (
                <span className="text-[9px] uppercase font-bold text-zinc-500">{isNatHoliday ? "🎉" : ""}</span>
              ) : !active ? (
                <span aria-hidden="true">&nbsp;</span>
              ) : (
                <div className={`${textCls} text-xs leading-tight font-semibold`}>
                  <div className="flex items-center justify-center gap-1">
                    <span>{allocated}{minP > 0 && !optional ? <span className="opacity-50">/{minP}</span> : ""}</span>
                    {fullyStaffed && <span className="text-[9px]">✓</span>}
                    {understaffed && <span className="text-[9px]">⚠</span>}
                  </div>
                  {minC > 0 && <div className="text-[9px] opacity-70">🚗 {allocatedCars}/{minC}</div>}
                  {entry?.note && <div className="text-[9px] opacity-90" title={entry.note}>📝</div>}
                </div>
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div className="rounded-lg border border-zinc-200 overflow-auto bg-white" style={{ WebkitOverflowScrolling: "touch" }} data-testid="weekly-matrix">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-20 bg-zinc-100">
          <tr>
            <th className="sticky left-0 bg-zinc-100 z-30 px-2 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-zinc-600 border-r-2 border-zinc-200 min-w-[180px]">
              Regie / Proiect
            </th>
            {days.map((d) => {
              const hol = holidaysByDate[d.iso];
              const isNatHoliday = hol && hol.type === "national";
              const isSundayReal = d.dow === 6;
              const isSat = d.dow === 5;
              const blackCell = isSundayReal || isNatHoliday;
              // Există entries pentru ziua asta? (afișăm butonul TRIMITE doar când e ceva de partajat)
              const dayHasEntries = (entries || []).some((e) => {
                const sd = e.start_date || "";
                const ed = e.end_date || sd;
                return sd <= d.iso && d.iso <= ed;
              });
              return (
                <th
                  key={d.iso}
                  className={`px-1 py-1.5 text-center text-[10px] font-bold uppercase tracking-wider border-r border-zinc-200 ${blackCell ? "bg-zinc-900 text-zinc-300" : isSat ? "bg-amber-50 text-amber-800" : "text-zinc-700"}`}
                >
                  <div>{DOW_HEADERS[d.dow]}</div>
                  <div className="text-[11px] mt-0.5">{fmtRoDay(d.date).split(" ").pop()}</div>
                  {onShareDay && dayHasEntries && !blackCell && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onShareDay(d.iso); }}
                      className="mt-1 inline-flex items-center justify-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white bg-green-600 hover:bg-green-700 rounded py-1 px-1.5 transition-colors w-full"
                      title="Trimite planul zilei pe WhatsApp"
                      data-testid={`wm-share-day-${d.iso}`}
                    >
                      📤 Trimite
                    </button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {regii.length > 0 && (
            <tr className="bg-amber-50/40">
              <td colSpan={1 + days.length} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-amber-800 border-y border-amber-200">
                🟧 Regii ({regii.length})
              </td>
            </tr>
          )}
          {regii.map((r) => renderRow(r, "regie"))}
          {projects.length > 0 && (
            <tr className="bg-blue-50/40">
              <td colSpan={1 + days.length} className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-blue-800 border-y border-blue-200">
                📁 Proiecte ({projects.length})
              </td>
            </tr>
          )}
          {projects.map((p) => renderRow(p, "project"))}
          {regii.length === 0 && projects.length === 0 && (
            <tr>
              <td colSpan={1 + days.length} className="text-center text-sm text-zinc-400 italic py-6">
                Nicio regie sau proiect activ în această săptămână.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
